import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { Search, MapPin, Calendar, Users, Filter, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { getDateLocale } from '../../lib/date-locale';
import { useExperience } from '../../context/ExperienceContext';
import { mv3 } from '../../styles/MV3_Styles';

interface SearchResult {
  id: string;
  type: 'trip' | 'location' | 'user';
  title: string;
  subtitle?: string;
  date?: string;
  image?: string;
  url: string;
}

interface SearchCtx {
  open: () => void;
  close: () => void;
  isOpen: boolean;
}

const Ctx = createContext<SearchCtx>({ open: () => {}, close: () => {}, isOpen: false });

export function SearchProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setIsOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <Ctx.Provider value={{ open, close, isOpen }}>
      {children}
      {isOpen && <SearchOverlay onClose={close} />}
    </Ctx.Provider>
  );
}

export const useSearch = () => useContext(Ctx);

function SearchOverlay({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('pages');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ trips: true, locations: true, users: true });
  const [showFilters, setShowFilters] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMangaV3 } = useExperience();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }

    const debounce = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(debounce);
  }, [query, filters]);

  const performSearch = async (searchQuery: string) => {
    setLoading(true);
    try {
      const searchResults: SearchResult[] = [];

      if (filters.trips && user) {
        const { data: trips } = await supabase
          .from('trips')
          .select('id, title, start_date, end_date, banner_url')
          .eq('user_id', user.id)
          .ilike('title', `%${searchQuery}%`)
          .limit(5);

        trips?.forEach((trip) => {
          searchResults.push({
            id: trip.id,
            type: 'trip',
            title: trip.title,
            subtitle: `${format(new Date(trip.start_date), 'MMM d', { locale: getDateLocale() })} - ${format(
              new Date(trip.end_date),
              'MMM d, yyyy',
              { locale: getDateLocale() }
            )}`,
            date: trip.start_date,
            image: trip.banner_url,
            url: `/trip-planner/${trip.id}`,
          });
        });
      }

      if (filters.locations && user) {
        // User's own locations
        const { data: ownLocations } = await supabase
          .from('location_cards')
          .select('id, name, address, image_url')
          .eq('scope', 'user')
          .eq('user_id', user.id)
          .ilike('name', `%${searchQuery}%`)
          .limit(5);

        // Curated locations assigned to user (manual + auto-assign collections)
        const { data: assigned } = await supabase
          .from('card_assignments')
          .select('card:location_cards!inner(id, name, address, image_url)')
          .eq('user_id', user.id)
          .ilike('card.name', `%${searchQuery}%`)
          .limit(5);

        const seen = new Set<string>();
        const allLocations = [
          ...(ownLocations || []),
          ...(assigned || []).map((a: any) => a.card).filter(Boolean),
        ];

        allLocations.forEach((location) => {
          if (seen.has(location.id)) return;
          seen.add(location.id);
          searchResults.push({
            id: location.id,
            type: 'location',
            title: location.name,
            subtitle: location.address,
            image: location.image_url,
            url: `/dream`,
          });
        });
      }

      if (filters.users) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name, username, avatar_url')
          .or(`full_name.ilike.%${searchQuery}%,username.ilike.%${searchQuery}%`)
          .limit(5);

        profiles?.forEach((profile) => {
          searchResults.push({
            id: profile.id,
            type: 'user',
            title: profile.full_name || profile.username || 'User',
            subtitle: profile.username ? `@${profile.username}` : undefined,
            image: profile.avatar_url,
            url: `/profile/${profile.id}`,
          });
        });
      }

      setResults(searchResults);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.url);
    onClose();
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'trip':
        return <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" style={isMangaV3 ? { color: '#E8192C' } : undefined} />;
      case 'location':
        return <MapPin className="w-5 h-5 text-green-600 dark:text-green-400" style={isMangaV3 ? { color: '#0a0a0a' } : undefined} />;
      case 'user':
        return <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" style={isMangaV3 ? { color: '#8b7e6a' } : undefined} />;
      default:
        return <Search className="w-5 h-5 text-gray-600 dark:text-gray-400" style={isMangaV3 ? { color: '#0a0a0a' } : undefined} />;
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[9998] flex justify-center" style={{ paddingTop: '20vh' }} onClick={handleBackdropClick}>
      <div className="fixed inset-0 bg-black/30" />
      <div ref={panelRef} className="relative w-[90vw] max-w-[600px] h-fit">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900/50 border border-gray-200 dark:border-gray-700 overflow-hidden" style={isMangaV3 ? mv3.modal : undefined}>
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Search className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('globalSearch.placeholder')}
                className="flex-1 outline-none bg-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                autoFocus
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  style={isMangaV3 ? { borderRadius: 2 } : undefined}
                >
                  <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </button>
              )}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg transition-colors ${
                  showFilters ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
                style={isMangaV3 ? (showFilters ? { borderRadius: 2, background: '#E8192C', color: '#ffffff' } : { borderRadius: 2 }) : undefined}
              >
                <Filter className="w-4 h-4" />
              </button>
            </div>

            {showFilters && (
              <div className="flex gap-4 mt-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.trips}
                    onChange={(e) => setFilters({ ...filters, trips: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    style={isMangaV3 ? { borderRadius: 0, accentColor: '#E8192C' } : undefined}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('globalSearch.filterTrips')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.locations}
                    onChange={(e) => setFilters({ ...filters, locations: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    style={isMangaV3 ? { borderRadius: 0, accentColor: '#E8192C' } : undefined}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('globalSearch.filterLocations')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.users}
                    onChange={(e) => setFilters({ ...filters, users: e.target.checked })}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    style={isMangaV3 ? { borderRadius: 0, accentColor: '#E8192C' } : undefined}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{t('globalSearch.filterPeople')}</span>
                </label>
              </div>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">{t('globalSearch.searching')}</div>
            ) : results.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                {query.trim() ? t('globalSearch.noResults') : t('globalSearch.startTyping')}
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {results.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleResultClick(result)}
                    className="w-full p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-3 text-left"
                  >
                    <div className="flex-shrink-0">
                      {result.image ? (
                        <img
                          src={result.image}
                          alt={result.title}
                          className="w-12 h-12 rounded-lg object-cover"
                          style={isMangaV3 ? { borderRadius: 2 } : undefined}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center" style={isMangaV3 ? { borderRadius: 2, border: '2px solid #0a0a0a', background: '#ffffff' } : undefined}>
                          {getIcon(result.type)}
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">{result.title}</p>
                      {result.subtitle && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{result.subtitle}</p>
                      )}
                    </div>

                    <div className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500 uppercase">
                      {t(`globalSearch.type${result.type.charAt(0).toUpperCase() + result.type.slice(1)}` as any)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-center gap-3">
            <span>{t('globalSearch.shortcutOpen')}</span>
            <span>·</span>
            <span>{t('globalSearch.shortcutClose')}</span>
            <span>·</span>
            <span>{t('globalSearch.shortcutNavigate')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
